import { useState, useEffect, useRef, useMemo } from 'react';
import type { CallBackProps, Step } from 'react-joyride';
import { STATUS } from 'react-joyride';
import {
  checkAndMarkOnboardingComplete,
  markTourCompleteOnServer,
  STEP_KEYS,
  getSavedStep,
  saveStep,
  clearStep,
  waitForOnboardingSync,
} from '../../../../../utils/onboarding';

/**
 * useSessionsTour encapsulates all react-joyride state and callbacks for the
 * Sessions page onboarding tour, keeping SessionsTab.tsx focused on layout.
 *
 * Returns everything SessionsTab needs to wire up the <Joyride> component and
 * the "Repetir guía" button.
 */

// Module-level flag so the tooltip renderer can access the skip state without
// prop-drilling through the Joyride tooltipComponent callback.
export let sessionsCanSkip = false;

export type UseSessionsTourOptions = {
  sessionsLoaded: boolean;
};

export type UseSessionsTourResult = {
  runTour: boolean;
  tourKey: number;
  tourStepIndex: number;
  steps: Step[];
  canSkip: boolean;
  onTourCallback: (data: CallBackProps) => void;
  onReplayTour: () => void;
};

export function useSessionsTour({ sessionsLoaded }: UseSessionsTourOptions): UseSessionsTourResult {
  const [runTour, setRunTour] = useState<boolean>(false);
  const [tourKey, setTourKey] = useState<number>(0);
  const [tourStepIndex, setTourStepIndex] = useState<number>(() => {
    const saved = getSavedStep(STEP_KEYS.sessions);
    return saved >= 0 ? saved : -1;
  });
  const [canSkip, setCanSkip] = useState(false);

  // Nav tour now runs in Home; in sessions it is considered already complete.
  const navTourComplete = true;

  const steps: Step[] = useMemo(
    () => [
      {
        target: '#sessions-week-selector',
        content:
          'Aquí por defecto veras la semana actual\nY debajo las sesiones que tuviste efectivamente (No cuentan las canceladas).',
        disableBeacon: true,
      },
      {
        target: '#sessions-day-timeline-container',
        content:
          'Cuando no haya sesiones, verás este estado vacío. Podés usar "Agendar sesión" para ir a tu agenda y crear una sesion.',
        disableBeacon: true,
      },
      {
        target: '#sess-tour-past',
        content: 'Así se ven las sesiones pasadas: en gris para diferenciarlas rápidamente.',
        disableBeacon: true,
      },
      {
        target: '#sess-tour-next',
        content:
          'Esta es la sesión próxima. Verás un estilo destacado para identificarla fácil.',
        disableBeacon: true,
      },
      {
        target: '#sess-tour-next .chips',
        content:
          'Estos son los chips: "Primera vez" y "Próxima sesión" para identificar estado y contexto.',
        disableBeacon: true,
      },
      {
        target: '#sess-tour-next .right-stack',
        content:
          'Desde cada sesión podrás realizar acciones como ingresar a la videollamada o abrir el detalle de paciente.',
        disableBeacon: true,
      },
      {
        target: '#sess-tour-cancelled',
        content:
          'Aquí verás las sesiones canceladas agrupadas. Hacé click para expandir y ver el detalle de cada una.',
        disableBeacon: true,
      },
    ],
    [],
  );

  useEffect(() => {
    let cancelled = false;
    if (sessionsLoaded && navTourComplete) {
      waitForOnboardingSync().then(() => {
        if (cancelled) return;
        try {
          const done = localStorage.getItem('psi-sessions-tour-done');
          if (!done) {
            sessionsCanSkip = false;
            setCanSkip(false);
            const saved = getSavedStep(STEP_KEYS.sessions);
            const startAt = saved >= 0 ? saved : 0;
            setTimeout(() => {
              if (!cancelled) {
                setTourStepIndex(startAt);
                setRunTour(true);
              }
            }, 250);
          } else {
            sessionsCanSkip = true;
            setCanSkip(true);
          }
        } catch {
          /* noop */
        }
      });
    }
    return () => {
      cancelled = true;
    };
  }, [sessionsLoaded, navTourComplete]);

  // Persist current step so the tour can resume after a page reload.
  useEffect(() => {
    if (runTour && tourStepIndex >= 0) saveStep(STEP_KEYS.sessions, tourStepIndex);
  }, [tourStepIndex, runTour]);

  const tourTargetObsRef = useRef<MutationObserver | null>(null);
  const tourPausingRef = useRef<boolean>(false);

  // Cleanup observer on unmount.
  useEffect(() => {
    return () => {
      if (tourTargetObsRef.current) {
        tourTargetObsRef.current.disconnect();
        tourTargetObsRef.current = null;
      }
    };
  }, []);

  const onTourCallback = (data: CallBackProps) => {
    const { status, type, action, index } = data as CallBackProps & {
      action?: string;
      index?: number;
    };

    // Ignore callbacks triggered by an intentional pause.
    if (tourPausingRef.current && (type === 'tour:end' || status === STATUS.FINISHED)) {
      return;
    }

    if (type === 'step:after') {
      if (action === 'next' || action === 'prev') {
        const nextIdx =
          action === 'next'
            ? (typeof index === 'number' ? index + 1 : 0)
            : (typeof index === 'number' ? Math.max(0, index - 1) : 0);

        if (nextIdx >= steps.length) {
          setTourStepIndex(-1);
          setRunTour(false);
          clearStep(STEP_KEYS.sessions);
          const wasAlreadyDone = localStorage.getItem('psi-sessions-tour-done') === '1';
          try {
            localStorage.setItem('psi-sessions-tour-done', '1');
          } catch {
            /* noop */
          }
          checkAndMarkOnboardingComplete();
          if (!wasAlreadyDone) markTourCompleteOnServer('psi-sessions-tour-done');
          return;
        }

        tourPausingRef.current = true;
        setRunTour(false);
        setTourStepIndex(nextIdx);

        const targetSelector = steps[nextIdx]?.target as string | undefined;

        if (tourTargetObsRef.current) {
          tourTargetObsRef.current.disconnect();
          tourTargetObsRef.current = null;
        }

        const resume = () => {
          tourPausingRef.current = false;
          setRunTour(true);
        };

        if (!targetSelector) {
          requestAnimationFrame(resume);
          return;
        }

        requestAnimationFrame(() => {
          if (document.querySelector(targetSelector)) {
            resume();
            return;
          }
          let fallbackTimer: ReturnType<typeof setTimeout> | null = null;
          const obs = new MutationObserver(() => {
            if (document.querySelector(targetSelector)) {
              obs.disconnect();
              tourTargetObsRef.current = null;
              if (fallbackTimer) clearTimeout(fallbackTimer);
              resume();
            }
          });
          obs.observe(document.body, { childList: true, subtree: true });
          tourTargetObsRef.current = obs;
          // Fallback: if target never appears within 1.5s, resume anyway
          fallbackTimer = setTimeout(() => {
            obs.disconnect();
            tourTargetObsRef.current = null;
            resume();
          }, 1500);
        });
        return;
      }
    }

    if (type === 'tour:end' || status === STATUS.SKIPPED) {
      setTourStepIndex(-1);
    }

    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setRunTour(false);
      clearStep(STEP_KEYS.sessions);
      const wasAlreadyDone = localStorage.getItem('psi-sessions-tour-done') === '1';
      try {
        localStorage.setItem('psi-sessions-tour-done', '1');
      } catch {
        /* noop */
      }
      checkAndMarkOnboardingComplete();
      if (!wasAlreadyDone) markTourCompleteOnServer('psi-sessions-tour-done');
    }
  };

  const onReplayTour = () => {
    clearStep(STEP_KEYS.sessions);
    setTourKey((k) => k + 1);
    setTourStepIndex(0);
    setRunTour(true);
  };

  return { runTour, tourKey, tourStepIndex, steps, canSkip, onTourCallback, onReplayTour };
}
